import {
  ConfirmationDialog,
  DataGrid,
  FilterToolbar,
  formatDate,
  StatusBadge,
  useNotifications,
  type GridQuery
} from '@ijoin/components';
import {
  GetAdminFlywayMigrationsParams,
  useDeleteAdminFlywayMigration,
  useGetAdminFlywayMigrations
} from '@ijoin/hooks';
import { Replay } from '@mui/icons-material';
import { Alert, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

import { camelCase } from 'lodash';
import { useState } from 'react';
import { useToggle } from 'usehooks-ts';

type FlywayMigration = {
  description: string;
  execution_time: number;
  installed_by: string;
  installed_on: string;
  installed_rank: number;
  script: string;
  success: boolean;
  version?: string;
};

const FlywayPage = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const [query, setQuery] = useState<GridQuery>();
  const [selectedMigration, setSelectedMigration] =
    useState<FlywayMigration | null>(null);
  const [replayOpen, , setReplayOpen] = useToggle(false);

  const migrations = useGetAdminFlywayMigrations(
    query as GetAdminFlywayMigrationsParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );

  const deleteMigration = useDeleteAdminFlywayMigration({
    mutation: {
      onError: () => notifications.error('Failed to re-apply migration'),
      onSuccess: data => {
        queryClient.invalidateQueries({
          queryKey: ['/api/v3/admin/flyway/migrations']
        });
        notifications.success(
          `Removed ${data.removed} history rows from ${data.version} up; they re-apply on the next Flyway run`
        );
      }
    }
  });

  const onReplayClose = () => {
    setReplayOpen(false);
    setSelectedMigration(null);
  };

  const onConfirmReplay = () => {
    if (!selectedMigration) return;

    deleteMigration.mutate({
      installedRank: selectedMigration.installed_rank
    });
    onReplayClose();
  };

  const columns: GridColDef<FlywayMigration>[] = [
    {
      field: 'version',
      flex: 0.6,
      headerName: 'Version',
      valueFormatter: value => value ?? '-'
    },
    {
      field: 'description',
      flex: 2,
      headerName: 'Description'
    },
    {
      field: 'script',
      flex: 2,
      headerName: 'Script'
    },
    {
      field: 'installed_by',
      flex: 1,
      headerName: 'Installed By'
    },
    {
      field: 'installed_on',
      flex: 1.3,
      headerName: 'Installed On',
      valueFormatter: value =>
        value ? formatDate(value, { format: 'MM/DD/YYYY h:mm:ss A' }) : '-'
    },
    {
      align: 'right',
      field: 'execution_time',
      flex: 0.8,
      headerAlign: 'right',
      headerName: 'Duration',
      valueFormatter: value => `${value} ms`
    },
    {
      field: 'success',
      flex: 0.7,
      headerName: 'Status',
      renderCell: params => (
        <StatusBadge severity={params.row.success ? 'success' : 'error'}>
          {params.row.success ? 'Success' : 'Failed'}
        </StatusBadge>
      ),
      sortable: false
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <Tooltip title='Re-apply this migration'>
          <IconButton
            data-testid={camelCase(`replay ${params.row.version}`)}
            onClick={() => {
              setSelectedMigration(params.row);
              setReplayOpen(true);
            }}>
            <Replay />
          </IconButton>
        </Tooltip>
      ),
      sortable: false,
      width: 65
    }
  ];

  return (
    <Stack spacing={2} sx={{ height: '100%', p: 2 }}>
      <Typography variant='h5'>Flyway Migrations</Typography>

      <Alert data-testid='flywayReplayWarning' severity='error'>
        Re-applying deletes the chosen version{' '}
        <strong>and every version above it</strong> from{' '}
        <code>flyway_schema_history</code>, so the next Flyway deploy re-runs
        all of them in order. The SQL is re-executed by that deploy, not by this
        page. Every migration in that range must be safe to run twice — one that
        is not will fail the deploy and leave a <code>success=0</code> row
        behind.
      </Alert>

      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={[
          { label: 'Search', name: 'search', type: 'text' },
          {
            label: 'Status',
            name: 'status',
            options: [
              { label: 'Success', value: 'success' },
              { label: 'Failed', value: 'failed' }
            ],
            type: 'select'
          }
        ]}
        getRowId={row => row.installed_rank}
        initialSort={[{ field: 'installed_rank', sort: 'desc' }]}
        loading={!query || migrations.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        rowCount={migrations.data?.pagination?.total ?? 0}
        rows={migrations.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'flywayMigrations'
          },
          toolbar: {
            onRefresh: migrations.refetch,
            searchButtonTestId: 'search',
            triggerTestId: 'flywayFilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />

      <ConfirmationDialog
        confirmationText={selectedMigration?.version ?? ''}
        confirmColor='error'
        confirmText='Re-apply'
        data-testid='replayMigration'
        isOpen={replayOpen}
        message={
          <Typography>
            This deletes the history rows for{' '}
            <strong>{selectedMigration?.script}</strong> and every version above
            it, so the next Flyway run re-applies that whole range in order.
          </Typography>
        }
        onCancel={onReplayClose}
        onConfirm={onConfirmReplay}
        title='Re-apply Migration'
      />
    </Stack>
  );
};

export const Route = createFileRoute('/flyway/')({
  beforeLoad: () => {
    if (localStorage.getItem('isAdmin') !== '1') {
      throw redirect({ to: '/organizations' });
    }
  },
  component: FlywayPage
});
